export default function checkContentType(customUrl) {
  const isImage = isImageUrl(customUrl);
  const googleUrl = convertToGooglePreviewOrMobileBasicUrl(customUrl);
  const googleDriveUrl = convertGoogleDriveUrl(customUrl);
  const youtubeUrl = convertToYouTubeEmbedUrl(customUrl);
  const loomUrl = convertToLoomEmbedUrl(customUrl);
  const airtableUrl = validateAirtableUrl(customUrl);
  const googleMapsUrl = convertToGoogleMapsEmbed(customUrl);
  const canvaUrl = convertToCanvaEmbedUrl(customUrl);
  const googleCalendarUrl = convertToGoogleCalendarEmbedUrl(customUrl);
  const vimeoUrl = convertVimeoToEmbed(customUrl);
  const instagramEmbedCode = convertInstagramReelToEmbedUrl(customUrl);
  const tiktokEmbedCode = convertTikTokToEmbedUrl(customUrl);
  const isPDF = isPdfUrl(customUrl);
  const isLogictryUrl = validateLogictryUrl(customUrl);
  return { isImage, googleUrl, googleDriveUrl, youtubeUrl, loomUrl, airtableUrl, googleMapsUrl, canvaUrl, googleCalendarUrl, vimeoUrl, instagramEmbedCode, tiktokEmbedCode, isPDF, isLogictryUrl };
}
function validateLogictryUrl(url) {
  try {
    // Parse the URL
    const urlParts = new URL(url);

    // Ensure it's a Google Docs URL
    if (!urlParts.hostname.includes("logictry.com")) {
      return null
    }
    return url;
  } catch {
    return null;
  }
}
function convertGoogleDriveUrl(url) {
  try {
    const urlParts = new URL(url);

    const hostname = urlParts.hostname;
    const pathname = urlParts.pathname;

    // Check if it's a Google Drive folder
    if (hostname.includes("drive.google.com")) {
      // Handle shared folder link
      if (pathname.includes("/drive/folders/")) {
        const regex = /\/folders\/([a-zA-Z0-9_-]+)/;
        const match = pathname.match(regex);
        if (match && match[1]) {
          const folderId = match[1];
          return `https://drive.google.com/embeddedfolderview?id=${folderId}#grid`;
        }
      }

      // Handle file sharing view (not a folder)
      if (pathname.includes("/file/d/")) {
        throw new Error("This is a file, not a folder. Cannot embed.");
      }
    }
    throw new Error("Unsupported Google Docs URL");
  } catch {
    return null;
  }
}
function convertToGooglePreviewOrMobileBasicUrl(url) {
  try {
    const urlParts = new URL(url);
    const hostname = urlParts.hostname;
    const pathname = urlParts.pathname;
    const queryParams = urlParts.search; // includes "?"
    const hashFragment = urlParts.hash;  // includes "#"

    // Accept both docs.google.com and drive.google.com
    const isDocsHost = hostname.includes("docs.google.com");
    const isDriveHost = hostname.includes("drive.google.com");

    if (!isDocsHost && !isDriveHost) {
      throw new Error("URL is not a supported Google Docs or Drive URL");
    }

    // Return as-is if it's already a preview for published Docs (e.g., forms)
    if (pathname.includes("/d/e/")) {
      return url;
    }

    // Match the file/document/spreadsheet ID
    const regex = /\/d\/([a-zA-Z0-9-_]+)/;
    const match = url.match(regex);
    if (!match || match.length < 2) {
      throw new Error("Invalid URL format");
    }

    const id = match[1];

    // Handle Google Drive file previews
    if (isDriveHost && pathname.includes("/file/")) {
      return `https://drive.google.com/file/d/${id}/preview${queryParams}${hashFragment}`;
    }

    // Handle Google Docs or Sheets
    const isSpreadsheet = pathname.includes("/spreadsheets/");
    const isDocument = pathname.includes("/document/");

    if (isSpreadsheet) {
      return `https://docs.google.com/spreadsheets/d/${id}/preview${queryParams}${hashFragment}`;
    } else if (isDocument) {
      return `https://docs.google.com/document/d/${id}/preview${queryParams}${hashFragment}`;
    }

    throw new Error("Unsupported Google Docs path");
  } catch {
    return null;
  }
}
function convertToYouTubeEmbedUrl(url) {
  try {
    // Parse the URL
    const urlParts = new URL(url);

    // Validate it's a YouTube URL
    if (!urlParts.hostname.includes("youtube.com") && !urlParts.hostname.includes("youtu.be")) {
      throw new Error("Invalid YouTube URL");
    }

    let videoId;

    // Handle different URL formats
    if (urlParts.hostname === "youtu.be") {
      videoId = urlParts.pathname.slice(1);
    } else if (urlParts.pathname.startsWith("/watch") && urlParts.searchParams.has("v")) {
      videoId = urlParts.searchParams.get("v");
    } else if (urlParts.pathname.startsWith("/embed/") || urlParts.pathname.startsWith("/shorts/")) {
      videoId = urlParts.pathname.split("/")[2];
    } else {
      throw new Error("No valid video ID found in the URL");
    }

    // Construct the embed URL
    const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`);

    // Add query parameters (excluding those irrelevant to embed format)
    urlParts.searchParams.forEach((value, key) => {
      embedUrl.searchParams.append(key, value);
    });

    return embedUrl.toString();
  } catch {
    return null; // Return null if the URL is invalid
  }
}
function convertToLoomEmbedUrl(url) {
  try {
    const urlParts = new URL(url);
    if (!urlParts.hostname.includes("loom.com")) {
      throw new Error("Invalid YouTube URL");
    }
    let videoId;
    if (urlParts.pathname.startsWith("/embed/") || urlParts.pathname.startsWith("/share/")) {
      videoId = urlParts.pathname.split("/")[2];
    } else {
      throw new Error("No valid video ID found in the URL");
    }
    const embedUrl = new URL(`https://www.loom.com/embed/${videoId}`);
    return embedUrl.toString();
  } catch {
    return null; // Return null if the URL is invalid
  }
}
function isPdfUrl(url) {
  try {
    // Parse the URL
    const urlParts = new URL(url);

    // Check if the pathname ends with ".pdf" (case-insensitive)
    return urlParts.pathname.toLowerCase().endsWith(".pdf");
  } catch {
    return false; // Return false if the URL is invalid
  }
}
function validateAirtableUrl(url) {
  try {
    // Check if URL is a valid Airtable URL
    const airtablePattern = /^https:\/\/airtable\.com\//;
    if (!airtablePattern.test(url)) {
      return null; // Not an Airtable URL
    }

    // Check if the URL is a properly formatted embed URL
    const embedPattern = /https:\/\/airtable\.com\/embed\/[a-zA-Z0-9]+\/[a-zA-Z0-9]+/;
    if (embedPattern.test(url)) {
      return true; // Proper embed URL
    }

    // Check for an improper Airtable URL (base or view without embed)
    const baseViewPattern = /https:\/\/airtable\.com\/(?:tbl|viw)[a-zA-Z0-9]+\/[a-zA-Z0-9]+/;
    if (baseViewPattern.test(url)) {
      return false; // Improper Airtable URL (Not Ready for Embed)
    }

    return null; // If no known pattern matches, return null (it is not an Airtable URL)
  } catch {
    return null; // Return null if there is an error
  }
}
function convertToGoogleMapsEmbed(url) {
  try {
    // If the URL is already in the correct embed format with mid
    const embedPattern = /^https:\/\/www\.google\.com\/maps\/d\/embed\?mid=([a-zA-Z0-9-_]+)/;
    if (embedPattern.test(url)) {
      return url; // Return the URL as is if it is already in embed format
    }

    // Check if the URL is a valid Google Maps viewer URL with a mid
    const googleMapsPattern = /^https:\/\/www\.google\.com\/maps\/d\/u\/0\/viewer\?/;
    if (!googleMapsPattern.test(url)) {
      return null; // Not a Google Maps URL with a valid mid
    }

    // Match the mid parameter for a valid embed URL (example: https://www.google.com/maps/d/u/0/viewer?mid=...)
    const midPattern = /[?&]mid=([a-zA-Z0-9-_]+)/;
    const midMatch = url.match(midPattern);

    if (midMatch) {
      const mid = midMatch[1]; // Extract map ID
      return `https://www.google.com/maps/d/embed?mid=${mid}`; // Embed format with mid
    }

    // If no mid is found, return null (it's not a valid embed URL)
    return null;
  } catch {
    return null; // Return null in case of error
  }
}
function isImageUrl(input) {
  if (!input) {
    return false; // Handle null or undefined input
  }

  // Common image file extensions
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff', 'ico', 'avif'];

  // Check if the input contains a MIME type or file extension
  const lowerInput = input.toLowerCase();

  // Check by file extension
  const extensionMatch = lowerInput.match(/\.(\w+)$/);
  if (extensionMatch) {
    const extension = extensionMatch[1]; // Extract the file extension
    if (imageExtensions.includes(extension)) {
      return true;
    }
  }

  // Check by MIME type
  if (lowerInput.startsWith('image/')) {
    return true;
  }

  return false; // Not an image
}
function convertToCanvaEmbedUrl(url) {
  try {
    // Regular expression to match Canva design URLs
    const regex = /https:\/\/www\.canva\.com\/design\/([a-zA-Z0-9-_]+)\/([a-zA-Z0-9-_]+)(?:\/[a-zA-Z]+)?/;
    const match = url.match(regex);

    // Check if the URL matches the expected pattern
    if (match && match.length >= 3) {
      const designId = match[1];
      const viewId = match[2];

      // Return the proper embed URL
      return `https://www.canva.com/design/${designId}/${viewId}/view?embed`;
    }

    // If not a valid Canva design URL, return null
    return null;
  } catch {
    return null;
  }
}
function convertToGoogleCalendarEmbedUrl(url) {
  try {
    // Parse the input URL
    const urlParts = new URL(url);

    // Ensure the URL is a Google Calendar URL
    if (!urlParts.hostname.includes("calendar.google.com")) {
      return null; // Not a Google Calendar URL
    }

    // Extract query parameters
    const params = new URLSearchParams(urlParts.search);

    // Check for required parameters
    const calendarId = params.get("src");
    const timezone = params.get("ctz");

    if (!calendarId) {
      throw new Error("Missing calendar ID (src) in the URL");
    }

    // Construct the embed URL
    const embedUrl = new URL("https://calendar.google.com/calendar/embed");
    embedUrl.searchParams.set("src", calendarId);

    // Set timezone if available
    if (timezone) {
      embedUrl.searchParams.set("ctz", timezone);
    }

    return embedUrl.toString();
  } catch {
    return null;
  }
}
function convertVimeoToEmbed(url) {
  const regex = /(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:video\/)?(\d+)(?:\/(\w+))?/;
  const match = url.match(regex);
  if (match && match[1]) {
    let embedUrl = `https://player.vimeo.com/video/${match[1]}`;
    // If a hash parameter (h=) is found, append it to the URL
    if (match[2]) {
      embedUrl += `?h=${match[2]}`;
    }
    return embedUrl;
  }
  return null;
}
function convertInstagramReelToEmbedUrl(url) {
  const regex = /(?:https?:\/\/)?(?:www\.)?instagram\.com\/reel\/([A-Za-z0-9_-]+)/;
  const match = url.match(regex);
  if (match && match[1]) {
    return `https://www.instagram.com/reel/${match[1]}/embed/captioned`;
  }
  return null; // Returns null if the URL doesn't match the pattern
}

function convertTikTokToEmbedUrl(url) {
  const regex = /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/(?:@[\w.-]+\/video\/(\d+))/;
  const match = url.match(regex);
  if (match && match[1]) {
    return `https://www.tiktok.com/embed/${match[1]}`;
  }
  return null; // Returns null if the URL doesn't match the pattern
}
